import type {
  ConnectorConnectionMode,
  ConnectorRecord,
  ConnectorTypeMetadata,
} from '@brunel/shared';
import { AI_CONNECTIONS_DISPLAY, type AiConnectionDisplay } from './ai-connections-catalog';
import {
  authStateLabel,
  installedLabel,
  type AiAuthState,
  type AiConnectionStatusRow,
} from './ai-connections-view';
import type {
  AiLocalRuntimeStatusRow,
  ModelOfferingRef,
  NodeOffering,
  NodePresence,
  ProviderUsageLatest,
} from './data';
import { buildSystemsMonitorCards, SYSTEM_CONNECTOR_TYPES, type SystemConnectorType } from './systems-monitor';

export type ExternalAiSurfaceKey = 'chatgpt' | 'grok';

/** Explicitly reported external AI bridge status (consumed by the AI usage tiles). */
export interface ExternalAiBridgeStatus {
  readonly surface: ExternalAiSurfaceKey | (string & {});
  readonly connected: boolean | null;
  readonly capability: 'read_only' | 'write_capable' | 'unavailable' | 'unknown';
  readonly source: string;
  readonly lastCheckedAt: string | null;
}

export type ConnectorCenterRowGroup = 'executable_runtime' | 'local_runtime' | 'external_ai' | 'service';
export type ConnectorCenterHealth = 'Healthy' | 'Attention' | 'Unavailable' | 'Unknown';

export interface ConnectorCenterRow {
  readonly id: string;
  readonly group: ConnectorCenterRowGroup;
  /** What kind of thing this is; never implies another surface is connected. */
  readonly kindLabel: string;
  readonly typeKey: string;
  readonly label: string;
  readonly provider: string;
  readonly subtitle: string;
  readonly nodeId: string | null;
  readonly nodeName: string | null;
  readonly connectionKey: string | null;
  /** Null only for an expected surface that has no canonical connector instance. */
  readonly connectorId: string | null;
  readonly sourceKey: string | null;
  readonly connectionMode: ConnectorConnectionMode | null;
  readonly statusLabel: string;
  readonly health: ConnectorCenterHealth;
  readonly healthLabel: string;
  readonly runtimeLabel: string;
  readonly models: readonly string[];
  readonly lastEvidenceAt: string | null;
  /** Provider allowance/usage. Sign-in is never treated as allowance proof. */
  readonly allowanceLabel: string;
  readonly sourceLabel: string;
  readonly note: string | null;
  readonly canOpenLocalDetail: boolean;
  readonly canRefreshConnector: boolean;
}

export interface ConnectorCenterGroup {
  readonly key: ConnectorCenterRowGroup;
  readonly title: string;
  readonly description: string;
  readonly rows: readonly ConnectorCenterRow[];
}

const ALLOWANCE_UNKNOWN = 'Unknown — sign-in is not allowance proof';
const NOT_SYNCHRONIZED_NOTE = 'Not in the canonical connector inventory yet. Brunel does not rebuild this row from local catalogs or probes.';

const PROVIDER_LABELS: Readonly<Record<string, string>> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  xai: 'xAI',
  ollama: 'Ollama',
};

function displayFor(connectionKey: string | null): AiConnectionDisplay | null {
  if (!connectionKey) return null;
  return AI_CONNECTIONS_DISPLAY.find((row) => row.connectionKey === connectionKey) ?? null;
}

function isOllama(display: AiConnectionDisplay): boolean {
  return display.connectionKey === 'ollama-local' || display.connectionKey === 'ollama-cloud';
}

/** A connector an external MCP session once created. External MCP was removed, so it is not shown. */
function isExternalMcp(connector: ConnectorRecord): boolean {
  return connector.connectionMode === 'external_mcp'
    || connector.capabilities.externalMcp === true
    || (connector.sourceKey ?? '').startsWith('ai-mcp:');
}

function detailText(connector: ConnectorRecord, key: string): string | null {
  const value = connector.healthDetail[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function detailBool(connector: ConnectorRecord, key: string): boolean | null {
  const value = connector.healthDetail[key];
  return typeof value === 'boolean' ? value : null;
}

function words(value: string): string {
  return value.replaceAll('_', ' ');
}

function usageFor(
  display: AiConnectionDisplay | null,
  nodeId: string | null,
  usage: readonly ProviderUsageLatest[],
): string | null {
  if (!display) return null;
  const row = usage
    .filter((candidate) =>
      candidate.provider_key === display.providerKey
      && candidate.pool_key === display.poolKey
      && (nodeId === null || candidate.node_id === null || candidate.node_id === nodeId))
    .sort((a, b) => b.observed_at.localeCompare(a.observed_at))[0];
  if (!row) return null;
  const parts: string[] = [];
  if (row.used_value !== null) parts.push(`${row.used_value} ${row.measure_unit} used`);
  if (row.remaining_value !== null) parts.push(`${row.remaining_value} ${row.measure_unit} remaining`);
  if (row.resets_at) parts.push(`resets ${row.resets_at}`);
  return parts.length > 0 ? parts.join(' · ') : 'Recorded reading has no stated figure';
}

function evidenceModels(
  connector: ConnectorRecord,
  display: AiConnectionDisplay | null,
  nodeOfferings: readonly NodeOffering[],
  modelOfferings: readonly ModelOfferingRef[],
): readonly string[] {
  const selected = connector.selectedModel ? [connector.selectedModel] : [];
  if (!display || !connector.selectedNodeId) return selected;
  const allowedRuntimeKeys = new Set(display.runtimeKeys);
  const byKey = new Map(modelOfferings.map((row) => [row.key, row] as const));
  const offered = nodeOfferings
    .filter((row) => row.node_id === connector.selectedNodeId)
    .map((row) => byKey.get(row.offering_key) ?? null)
    .filter((row): row is ModelOfferingRef => row !== null && allowedRuntimeKeys.has(row.runtime_key))
    .map((row) => row.model_key);
  return [...new Set([...selected, ...offered])].sort();
}

interface EvidenceInput {
  readonly nodes: readonly NodePresence[];
  readonly statuses: readonly AiConnectionStatusRow[];
  readonly localStatuses: readonly AiLocalRuntimeStatusRow[];
  readonly usage: readonly ProviderUsageLatest[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
  /** True when this connector read itself succeeded against the Supabase project. */
  readonly registryLive: boolean;
}

/** One node-bound AI connector: Codex, Claude, Grok Build, Grok Bot, Ollama. */
function nodeAiRow(connector: ConnectorRecord, display: AiConnectionDisplay | null, input: EvidenceInput): ConnectorCenterRow {
  const nodeId = connector.selectedNodeId;
  const node = nodeId ? input.nodes.find((row) => row.node_id === nodeId) ?? null : null;
  // The live probe row is the projection's own source, so it is never older than the connector.
  const live = nodeId && connector.aiConnectionKey
    ? input.statuses.find((row) => row.node_id === nodeId && row.connection_key === connector.aiConnectionKey) ?? null
    : null;
  const local = nodeId && connector.aiConnectionKey
    ? input.localStatuses.find((row) => row.node_id === nodeId && row.connection_key === connector.aiConnectionKey) ?? null
    : null;
  const sourceMissing = detailBool(connector, 'sourceMissing') === true && live === null;
  const installed = live?.installed ?? detailBool(connector, 'installed');
  const auth: AiAuthState = live?.auth_state
    ?? (connector.authState === 'pending' ? 'unknown' : connector.authState);
  const probeError = live ? live.last_error !== null : detailBool(connector, 'probeError') === true;
  const localNoAuth = connector.connectionMode === 'local_no_auth';
  const executable = display !== null && display.runtimeKeys.length > 0;
  const nodeName = node?.name ?? detailText(connector, 'nodeName');

  let health: ConnectorCenterHealth;
  let healthLabel: string;
  if (sourceMissing) {
    health = 'Unknown'; healthLabel = 'Unknown — probe evidence missing';
  } else if (node === null) {
    health = 'Unknown'; healthLabel = 'Unknown — computer not reporting';
  } else if (node.status === 'offline') {
    health = 'Unavailable'; healthLabel = 'Unavailable — computer offline';
  } else if (installed === false) {
    health = 'Unavailable'; healthLabel = 'Unavailable — not installed';
  } else if (probeError) {
    health = 'Attention'; healthLabel = 'Attention — last probe reported an error';
  } else if (localNoAuth) {
    if (local?.service_state === 'reachable') { health = 'Healthy'; healthLabel = 'Healthy — local service reachable'; }
    else if (local && local.service_state !== 'unknown') { health = 'Unavailable'; healthLabel = `Unavailable — service ${local.service_state}`; }
    else { health = 'Unknown'; healthLabel = 'Unknown — no local service probe'; }
  } else if (installed === true && auth === 'signed_in') {
    health = 'Healthy'; healthLabel = 'Healthy — installed and signed in on an online computer';
  } else if (installed === true && auth === 'signed_out') {
    health = 'Unavailable'; healthLabel = 'Unavailable — signed out';
  } else {
    health = 'Unknown'; healthLabel = 'Unknown — sign-in not proven';
  }

  const statusLabel = sourceMissing
    ? 'Probe evidence missing'
    : localNoAuth
      ? `${installedLabel(installed)} · No sign-in required`
      : `${installedLabel(installed)} · ${authStateLabel(auth)}`;
  const reading = usageFor(display, nodeId, input.usage);
  const localNote = local
    ? `Service ${local.service_state} · ${local.installed_models_known
      ? `${local.installed_models.length} installed model${local.installed_models.length === 1 ? '' : 's'}`
      : 'installed models Unknown'} · ${local.loaded_models_known ? `${local.loaded_models.length} loaded` : 'loaded models Unknown'}`
    : null;

  return {
    id: `connector:${connector.id}`,
    group: display && isOllama(display) ? 'local_runtime' : executable ? 'executable_runtime' : 'external_ai',
    kindLabel: executable
      ? (localNoAuth ? 'Local runtime' : 'Executable runtime')
      : 'External teammate · not executable',
    typeKey: connector.connectorType,
    label: connector.label,
    provider: display ? PROVIDER_LABELS[display.providerKey] ?? display.providerKey : connector.providerService || 'Unknown',
    subtitle: executable
      ? `${display!.displayLabel} on ${nodeName ?? 'an unreported computer'}. Installed is not signed in; signed in is not allowance proof.`
      : `${display?.displayLabel ?? connector.label}. No executable Brunel runtime is registered for this connection.`,
    nodeId,
    nodeName,
    connectionKey: connector.aiConnectionKey,
    connectorId: connector.id,
    sourceKey: connector.sourceKey ?? null,
    connectionMode: connector.connectionMode,
    statusLabel,
    health,
    healthLabel,
    runtimeLabel: executable
      ? connector.selectedRuntime ?? display!.runtimeKeys.join(' / ')
      : 'No executable Brunel runtime',
    models: evidenceModels(connector, display, input.nodeOfferings, input.modelOfferings),
    lastEvidenceAt: live?.last_probe_at ?? connector.lastHealthAt,
    allowanceLabel: localNoAuth ? 'Not applicable — local runtime' : reading ?? ALLOWANCE_UNKNOWN,
    sourceLabel: live
      ? 'Canonical connector + live AI connection probe'
      : 'Canonical connector (projected AI connection probe)',
    note: localNote,
    canOpenLocalDetail: connector.aiConnectionKey === 'ollama-local' && nodeId !== null,
    canRefreshConnector: connector.status !== 'revoked' && connector.capabilities.refresh,
  };
}

function supabaseNote(connector: ConnectorRecord): string {
  const management = detailText(connector, 'managementHealth');
  const enrolled = detailBool(connector, 'managementEnrolled');
  const nodeName = detailText(connector, 'designatedNodeName');
  const parts = [
    `Management credential: ${management ? words(management) : 'Unknown'}`,
    enrolled === null ? 'enrollment Unknown' : enrolled ? 'enrolled' : 'not enrolled',
  ];
  if (nodeName) parts.push(`designated computer ${nodeName}`);
  return `${parts.join(' · ')}. Management enrollment is reported separately from service health.`;
}

function serviceRow(
  connector: ConnectorRecord,
  type: ConnectorTypeMetadata | null,
  input: EvidenceInput,
): ConnectorCenterRow {
  const base = {
    id: `connector:${connector.id}`,
    group: 'service' as const,
    kindLabel: type?.displayLabel ?? connector.connectorType,
    typeKey: connector.connectorType,
    label: connector.label,
    provider: connector.providerService || 'Unknown',
    subtitle: type ? `${type.displayLabel} · ${type.category}` : `Connector type ${connector.connectorType}`,
    nodeId: connector.selectedNodeId,
    nodeName: connector.selectedNodeId
      ? input.nodes.find((row) => row.node_id === connector.selectedNodeId)?.name ?? null
      : null,
    connectionKey: connector.aiConnectionKey,
    connectorId: connector.id,
    sourceKey: connector.sourceKey ?? null,
    connectionMode: connector.connectionMode,
    runtimeLabel: 'Not applicable',
    models: [] as readonly string[],
    allowanceLabel: 'Not applicable',
    canOpenLocalDetail: false,
    canRefreshConnector: connector.status !== 'revoked' && connector.capabilities.refresh,
  };

  if (connector.connectorType === 'supabase_project') {
    // A successful registry read proves connectivity only. It does not measure the
    // database host's CPU, memory or Disk IO, so it must never become capacity health.
    return {
      ...base,
      statusLabel: `${connector.status} · management ${words(detailText(connector, 'managementHealth') ?? 'Unknown')}`,
      health: 'Unknown',
      healthLabel: input.registryLive
        ? 'Connected — this read reached the project; CPU, memory and Disk IO are not verified'
        : 'Unknown — connector read did not succeed',
      lastEvidenceAt: detailText(connector, 'lastVerifiedAt') ?? connector.lastHealthAt,
      sourceLabel: input.registryLive
        ? 'Canonical connector + current connector read'
        : 'Canonical connector only',
      note: supabaseNote(connector),
    };
  }

  const systemType = (SYSTEM_CONNECTOR_TYPES as readonly string[]).includes(connector.connectorType)
    ? connector.connectorType as SystemConnectorType
    : null;
  const card = systemType
    ? buildSystemsMonitorCards([connector]).find((row) => row.typeKey === systemType) ?? null
    : null;
  const status = card?.status
    ?? (connector.healthStatus === 'healthy' && connector.lastHealthAt ? 'Healthy'
      : connector.healthStatus === 'degraded' ? 'Attention'
        : connector.healthStatus === 'unavailable' || connector.connectionMode === 'unavailable' ? 'Unavailable'
          : 'Unknown');
  const health: ConnectorCenterHealth = status === 'Failing' ? 'Attention' : status;
  return {
    ...base,
    statusLabel: `${connector.status} · ${words(connector.authState)}`,
    health,
    healthLabel: card ? `${status} — ${card.summary}` : status,
    lastEvidenceAt: card?.observedAt ?? connector.lastHealthAt,
    sourceLabel: card?.source ?? 'Canonical connector registry',
    note: connector.connectionMode === 'unavailable'
      ? 'Credentials are not available to Brunel; health figures remain Unknown.'
      : null,
  };
}

function missingRow(input: {
  readonly id: string;
  readonly group: ConnectorCenterRowGroup;
  readonly kindLabel: string;
  readonly typeKey: string;
  readonly label: string;
  readonly provider: string;
  readonly subtitle: string;
  readonly connectionKey: string | null;
  readonly runtimeLabel: string;
  readonly note: string;
}): ConnectorCenterRow {
  return {
    ...input,
    nodeId: null,
    nodeName: null,
    connectorId: null,
    sourceKey: null,
    connectionMode: null,
    statusLabel: 'Not synchronized',
    health: 'Unknown',
    healthLabel: 'Unknown — no canonical evidence',
    models: [],
    lastEvidenceAt: null,
    allowanceLabel: 'Unknown',
    sourceLabel: 'No canonical connector instance',
    canOpenLocalDetail: false,
    canRefreshConnector: false,
  };
}

export function buildConnectorCenterGroups(input: {
  readonly registryTypes: readonly ConnectorTypeMetadata[];
  readonly connectors: readonly ConnectorRecord[];
  readonly vaultAvailable: boolean;
  /** Whether list_connectors succeeded for this snapshot. */
  readonly registryLoaded?: boolean;
  readonly nodes: readonly NodePresence[];
  readonly connectionStatuses: readonly AiConnectionStatusRow[];
  readonly localRuntimeStatuses: readonly AiLocalRuntimeStatusRow[];
  readonly providerUsage: readonly ProviderUsageLatest[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
}): readonly ConnectorCenterGroup[] {
  const evidence: EvidenceInput = {
    nodes: input.nodes,
    statuses: input.connectionStatuses,
    localStatuses: input.localRuntimeStatuses,
    usage: input.providerUsage,
    nodeOfferings: input.nodeOfferings,
    modelOfferings: input.modelOfferings,
    registryLive: input.registryLoaded === true,
  };
  const typeByKey = new Map(input.registryTypes.map((row) => [row.typeKey, row] as const));
  const active = input.connectors.filter((row) => row.status !== 'revoked' && !isExternalMcp(row));

  const rows: ConnectorCenterRow[] = active.map((connector) => {
    if (connector.connectorType !== 'ai_provider') {
      return serviceRow(connector, typeByKey.get(connector.connectorType) ?? null, evidence);
    }
    return nodeAiRow(connector, displayFor(connector.aiConnectionKey), evidence);
  });

  // Expected surfaces with no canonical instance stay visible as Unknown, never inferred.
  for (const display of AI_CONNECTIONS_DISPLAY) {
    if (active.some((row) => row.aiConnectionKey === display.connectionKey)) continue;
    const executable = display.runtimeKeys.length > 0;
    rows.push(missingRow({
      id: `missing:ai:${display.connectionKey}`,
      group: isOllama(display) ? 'local_runtime' : executable ? 'executable_runtime' : 'external_ai',
      kindLabel: executable
        ? (display.connectionMode === 'local_no_auth' ? 'Local runtime' : 'Executable runtime')
        : 'External teammate · not executable',
      typeKey: 'ai_provider',
      label: display.displayLabel,
      provider: PROVIDER_LABELS[display.providerKey] ?? display.providerKey,
      subtitle: executable
        ? 'Executable runtime on a specific computer. Installed is not signed in; signed in is not allowance proof.'
        : 'No executable Brunel runtime is registered for this connection.',
      connectionKey: display.connectionKey,
      runtimeLabel: executable ? display.runtimeKeys.join(' / ') : 'No executable Brunel runtime',
      note: NOT_SYNCHRONIZED_NOTE,
    }));
  }
  for (const type of input.registryTypes) {
    if (type.typeKey === 'ai_provider' || active.some((row) => row.connectorType === type.typeKey)) continue;
    rows.push(missingRow({
      id: `missing:type:${type.typeKey}`,
      group: 'service',
      kindLabel: type.displayLabel,
      typeKey: type.typeKey,
      label: type.displayLabel,
      provider: 'Unknown',
      subtitle: `Registered connector type · ${type.category}`,
      connectionKey: null,
      runtimeLabel: 'Not applicable',
      note: type.typeKey === 'supabase_project' && !input.vaultAvailable
        ? 'Metadata-only: secure vault credentials are unavailable, so project health stays Unknown.'
        : 'No active connector instance exists for this registered type.',
    }));
  }

  const groups: readonly Omit<ConnectorCenterGroup, 'rows'>[] = [
    {
      key: 'executable_runtime',
      title: 'Executable subscription runtimes',
      description: 'CLIs Brunel can execute on a specific computer. Installed is not signed in, and signed in is not allowance proof.',
    },
    {
      key: 'local_runtime',
      title: 'Ollama',
      description: 'Local and cloud Ollama per computer. Local Ollama never requires account sign-in.',
    },
    {
      key: 'external_ai',
      title: 'External AI · not executable',
      description: 'External teammates with no executable Brunel runtime.',
    },
    {
      key: 'service',
      title: 'Services & registered connector types',
      description: 'Canonical connector instances for registered types. Missing or stale evidence stays Unknown.',
    },
  ];
  return groups.map((group) => ({ ...group, rows: rows.filter((row) => row.group === group.key) }));
}
